import React, { useCallback, useRef } from 'react'
import { observer } from 'mobx-react'
import { Container } from 'typescript-ioc/es5'
import axios from 'axios'
import aqe from '@pushly/aqe'
import { SessionExpiryHost } from '@pushly/aqe/lib/components/session-expiry-host/session-expiry-host'
import { SessionState } from '@pushly/aqe'
import { AuthService } from '@halo/services/auth.service'

/**
 * MobX observer that reads session state from halocline's SessionState
 * and passes it as configuration to the shared SessionExpiryHost component.
 *
 * This adapter bridges halocline's MobX store shape to the framework-agnostic
 * UseSessionExpiryConfig interface consumed by the FSM-based orchestrator in AQE.
 */
export const SessionExpiryAdapter: React.FC = observer(() => {
    const authService = useRef(Container.get(AuthService)).current
    const sessionState = useRef(Container.get(SessionState)).current

    const handleExtend = useCallback(async (): Promise<boolean> => {
        try {
            await axios.post(`${aqe.defaults.publicApiDomain}/auth/refresh`, null, {
                bypassGlobalErrorHandlers: true,
                withCredentials: true,
                headers: { 'X-User-Activity': 'true' },
            })
            return true
        } catch {
            return false
        }
    }, [])

    const handleSignOut = useCallback(async (): Promise<void> => {
        await authService.logout()
    }, [authService])

    const handleReauth = useCallback((): void => {
        authService.logout(false)
    }, [authService])

    const handleExpired = useCallback((): void => {
        sessionState.sessionExpired = true
    }, [sessionState])

    return (
        <SessionExpiryHost
            idleExpiresAt={sessionState.idleExpiresAt}
            absoluteExpiresAt={sessionState.absoluteExpiresAt}
            idleTimeoutDuration={sessionState.idleTimeoutDuration}
            absoluteTimeoutDuration={sessionState.absoluteTimeoutDuration}
            isAuthenticated={sessionState.isAuthenticated}
            onExtend={handleExtend}
            onSignOut={handleSignOut}
            onReauth={handleReauth}
            onExpired={handleExpired}
        />
    )
})
